import React from 'react';
import PropTypes from 'prop-types';
import { EmailValidation, RequiredValidation, Label, Heading } from 'sarsaparilla';
import { get } from 'lodash';

import {
  ONLINE,
  PERMIT_ISSUED,
  PERMIT_COMPLETE,
  LOTTERY_AWARDED,
  PERMIT_IN_PROGRESS
} from 'ui-permit/src/constants';
import { salesChannelTypes, issuanceStatuses, getClassName } from 'ui-permit/src/utils/utils';

import {
  GROUP_DETAIL_PROP_PATH,
  GROUP_DETAIL_ADDRESS_PROP_PATH,
  GROUP_DETAIL_FIRST_NAME_PROP_PATH,
  GROUP_DETAIL_LAST_NAME_PROP_PATH,
  GROUP_DETAIL_MIDDLE_NAME_PROP_PATH,
  GROUP_DETAIL_ZIPCODE_PROP_PATH,
  GROUP_DETAIL_ADDRESS1_PROP_PATH,
  GROUP_DETAIL_ADDRESS2_PROP_PATH,
  GROUP_DETAIL_STATE_PROP_PATH,
  GROUP_DETAIL_CITY_PROP_PATH,
  GROUP_DETAIL_EMAIL_PROP_PATH,
  GROUP_DETAIL_PHONE_PROP_PATH
} from '../constants/propPaths';

const defaultDataBindingMap = {
  [GROUP_DETAIL_PROP_PATH]: 'permit_holder',
  [GROUP_DETAIL_ADDRESS_PROP_PATH]: 'permit_holder_address',
  [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
  [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
  [GROUP_DETAIL_MIDDLE_NAME_PROP_PATH]: 'middle_name',
  [GROUP_DETAIL_ZIPCODE_PROP_PATH]: 'postal_code',
  [GROUP_DETAIL_ADDRESS1_PROP_PATH]: 'street_address1',
  [GROUP_DETAIL_ADDRESS2_PROP_PATH]: 'street_address2',
  [GROUP_DETAIL_STATE_PROP_PATH]: 'state_code',
  [GROUP_DETAIL_CITY_PROP_PATH]: 'city',
  [GROUP_DETAIL_EMAIL_PROP_PATH]: 'email',
  [GROUP_DETAIL_PHONE_PROP_PATH]: 'phone'
};

const defaultAttributes = { requirePermissionCheck: true };

export default (issuanceShape, dataBindingsMap = defaultDataBindingMap, attributes = defaultAttributes) => {
  // create all prop mappings
  const dm = dataBindingsMap;
  const firstNamePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_FIRST_NAME_PROP_PATH]}`;
  const lastnamePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_LAST_NAME_PROP_PATH]}`;
  const middlenamePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_MIDDLE_NAME_PROP_PATH]}`;
  const postalCodePropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_ZIPCODE_PROP_PATH]}`;
  const address1PropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_ADDRESS1_PROP_PATH]}`;
  const address2PropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_ADDRESS2_PROP_PATH]}`;
  const stateCodePropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_STATE_PROP_PATH]}`;
  const cityPropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_CITY_PROP_PATH]}`;
  const emailPropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_EMAIL_PROP_PATH]}`;
  const phonePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_PHONE_PROP_PATH]}`;
  const saveToProfilePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.save_profile`;
  class GroupDetail extends React.Component {
    onFormValidate(isInValid, error, path) {
      if (isInValid) {
        this.props.pActions.formValidationChange(`${this.props.reservation.id}_${path}`, error);
      }
      else {
        this.props.pActions.formValidationChange(`${this.props.reservation.id}_${path}`, null);
      }
    }

    render() {
      const { pActions, reservation, hasPermission, isInternal, isReadOnly} = this.props;

      const { id } = reservation;
      const changeLeaderDisabled = reservation.sales_channel_type === salesChannelTypes.get(ONLINE);
      const firstName = get(this.props.reservation, firstNamePropPath, '');
      const lastName = get(this.props.reservation, lastnamePropPath, '');
      const addZipCode = get(this.props.reservation, postalCodePropPath, '');
      const streetAddress = get(this.props.reservation, address1PropPath, '');
      const addState = get(this.props.reservation, stateCodePropPath, '');
      const addCity = get(this.props.reservation, cityPropPath, '');
      const email = get(this.props.reservation, emailPropPath, '');
      const phone = get(this.props.reservation, phonePropPath, '');
      let disabled = true;
      if (hasPermission && isInternal && !isReadOnly) {
        disabled = false;
      }
      else {
        disabled = (
          (reservation.status === issuanceStatuses.get(PERMIT_ISSUED)) ||
          (reservation.status === issuanceStatuses.get(PERMIT_COMPLETE)) ||
          (reservation.status === issuanceStatuses.get(LOTTERY_AWARDED)) ||
          (reservation.status === issuanceStatuses.get(PERMIT_IN_PROGRESS) && !!reservation.lottery_id)
        );
      }

      const saveAccountLeaderProfile = get(this.props.reservation, saveToProfilePropPath, false);

      const firstNameValidation = getClassName(get(this.props.validation, `${id}_${firstNamePropPath}`), this.props.displayValidationError);
      const lastNameValidation = getClassName(get(this.props.validation, `${id}_${lastnamePropPath}`), this.props.displayValidationError);
      const addZipCodeReqValidation = getClassName(get(this.props.validation, `${id}_${postalCodePropPath}_req`), this.props.displayValidationError);
      const addZipCodeFormatValidation = getClassName(get(this.props.validation, `${id}_${postalCodePropPath}_format`), this.props.displayValidationError);
      const streetValidation = getClassName(get(this.props.validation, `${id}_${address1PropPath}`), this.props.displayValidationError);
      const addStateValidation = getClassName(get(this.props.validation, `${id}_${stateCodePropPath}`), this.props.displayValidationError);
      const addCityValidation = getClassName(get(this.props.validation, `${id}_${cityPropPath}`), this.props.displayValidationError);
      const emailReqValidation = getClassName(get(this.props.validation, `${id}_${emailPropPath}_req`), this.props.displayValidationError);
      const emailFormatValidation = getClassName(get(this.props.validation, `${id}_${emailPropPath}_format`), this.props.displayValidationError);
      const phoneValidation = getClassName(get(this.props.validation, `${id}_${phonePropPath}_format`), this.props.displayValidationError);
      const phoneReqValidation = getClassName(get(this.props.validation, `${id}_${phonePropPath}_req`), this.props.displayValidationError);

      const savePermitHolderControl = !disabled && (
        <div className="rec-grid-12-12">
          <div className="rec-form-item-wrap">
            <label htmlFor={`save-leader-checkbox_${id}`} className="rec-label-checkbox">
              <input
                id={`save-leader-checkbox_${id}`}
                className="rec-input-hide"
                type="checkbox"
                checked={saveAccountLeaderProfile}
                onChange={() => pActions.updatePendingReservation(id, saveToProfilePropPath, !saveAccountLeaderProfile)} />
              <span className="rec-input-checkbox" />
              <strong>Save above changes to my account.</strong>
            </label>
          </div>
        </div>
      );

      return (
        <div>
          <div className="permit-holder-wrapper rec-nested-wrap">
            <div className="rec-grid-12-12">
              <Heading headingLevel={2} appearance="h5" className="mb-1">Permit Holder</Heading>
            </div>
            <div className="rec-grid-5-12">
              <div className="rec-form-item-wrap">
                <Label isRequired htmlFor="first-name">First Name</Label>
                <input
                  id="first-name"
                  defaultValue={firstName}
                  disabled={disabled}
                  className={firstNameValidation}
                  onBlur={(e) => pActions.updatePendingReservation(id, firstNamePropPath, e.target.value)}
                  type="text" />
                <RequiredValidation
                  value={firstName}
                  displayInSummary
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, firstNamePropPath)}
                  controlLabel="First Name" />
              </div>
            </div>
            <div className="rec-grid-4-12">
              <div className="rec-form-item-wrap">
                <Label isRequired htmlFor="last-name">Last Name</Label>
                <input
                  id="last-name"
                  defaultValue={lastName}
                  disabled={disabled}
                  className={lastNameValidation}
                  onBlur={(e) => pActions.updatePendingReservation(id, lastnamePropPath, e.target.value)}
                  type="text" />
                <RequiredValidation
                  value={lastName}
                  displayInSummary
                  controlLabel="Last Name"
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, lastnamePropPath)} />
              </div>
            </div>
            <div className="rec-grid-2-12">
              <div className="rec-form-item-wrap">
              <Label htmlFor="middle-name">MI</Label>
                <input
                  id="middle-name"
                  disabled={disabled}
                  defaultValue={get(this.props.reservation, middlenamePropPath, '')}
                  onBlur={(e) => pActions.updatePendingReservation(id, middlenamePropPath, e.target.value)}
                  type="text" />
              </div>
            </div>
            <div className="rec-grid-5-12">
              <div className="rec-form-item-wrap">
                <Label isRequired htmlFor="street-address">Street Address</Label>
                <input
                  id="street-address"
                  defaultValue={streetAddress}
                  disabled={disabled}
                  className={streetValidation}
                  onBlur={(e) => pActions.updatePendingReservation(id, address1PropPath, e.target.value)}
                  type="text" />
                <RequiredValidation
                  value={streetAddress}
                  displayInSummary
                  controlLabel="Street"
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, address1PropPath)} />
              </div>
            </div>
            <div className="rec-grid-6-12">
              <div className="rec-form-item-wrap">
                <Label htmlFor="suite-number">Suite Number</Label>
                <input
                  id="suite-number"
                  disabled={disabled}
                  defaultValue={get(this.props.reservation, address2PropPath, '')}
                  onBlur={(e) => pActions.updatePendingReservation(id, address2PropPath, e.target.value)}
                  type="text" />
              </div>
            </div>
            <div className="rec-grid-5-12">
              <div className="rec-form-item-wrap">
                <Label isRequired htmlFor="city-name">City</Label>
                <input
                  id="city-name"
                  defaultValue={addCity}
                  disabled={disabled}
                  className={addCityValidation}
                  onBlur={(e) => pActions.updatePendingReservation(id, cityPropPath, e.target.value)}
                  type="text" />
                <RequiredValidation
                  value={addCity}
                  displayInSummary
                  controlLabel="City"
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, cityPropPath)} />
              </div>
            </div>
            <div className="rec-grid-4-12">
              <div className="rec-form-item-wrap">
                <Label isRequired htmlFor="state-name">State</Label>
                <input
                  id="state-name"
                  defaultValue={addState}
                  disabled={disabled}
                  className={addStateValidation}
                  onBlur={(e) => pActions.updatePendingReservation(id, stateCodePropPath, e.target.value)}
                  type="text" />
                <RequiredValidation
                  value={addState}
                  displayInSummary
                  controlLabel="State"
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, stateCodePropPath)} />
              </div>
            </div>
            <div className="rec-grid-2-12">
              <div className="rec-form-item-wrap">
                <Label isRequired htmlFor="zipcode-input">Zip Code</Label>
                <input
                  id="zipcode-input"
                  defaultValue={addZipCode}
                  disabled={disabled}
                  className={`${addZipCodeReqValidation}${addZipCodeFormatValidation}`}
                  onBlur={(e) => pActions.updatePendingReservation(id, postalCodePropPath, e.target.value)}
                  type="text" />
                <RequiredValidation
                  value={addZipCode}
                  displayInSummary
                  controlLabel="Zip Code"
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, `${postalCodePropPath}_req`)} />
              </div>
            </div>
            <div className="rec-grid-5-12">
              <div className="rec-form-item-wrap">
                <Label isRequired htmlFor="email-input">Email</Label>
                <input
                  id="email-input"
                  defaultValue={email}
                  disabled={changeLeaderDisabled || disabled}
                  className={`${emailReqValidation}${emailFormatValidation}`}
                  onBlur={(e) => pActions.updatePendingReservation(id, emailPropPath, e.target.value)}
                  type="text" />
                <RequiredValidation
                  value={email}
                  displayInSummary
                  controlLabel="Email"
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, `${emailPropPath}_req`)} />
                <EmailValidation
                  value={email}
                  displayInSummary
                  controlLabel="Email"
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, `${emailPropPath}_format`)} />
              </div>
            </div>
            <div className="rec-grid-6-12">
              <div className="rec-form-item-wrap">
                <Label isRequired htmlFor="mobile-phone">Mobile Phone</Label>
                <input
                  id="mobile-phone"
                  defaultValue={phone}
                  disabled={disabled}
                  className={`${phoneValidation}${phoneReqValidation}`}
                  onBlur={(e) => pActions.updatePendingReservation(id, phonePropPath, e.target.value)}
                  type="text" />
                <RequiredValidation
                  value={phone}
                  displayInSummary
                  controlLabel="Phone"
                  onValidated={(isInValid, error) => this.onFormValidate(isInValid, error, `${phonePropPath}_req`)} />
              </div>
            </div>
            {savePermitHolderControl}
          </div>
        </div>);
    }
  }

  GroupDetail.propTypes = {
    validation: PropTypes.shape({}).isRequired,
    reservation: issuanceShape,
    pActions: PropTypes.shape({
      formValidationChange: PropTypes.func.isRequired,
      updatePendingReservation: PropTypes.func.isRequired
    }).isRequired,
    displayValidationError: PropTypes.bool,
    hasPermission: PropTypes.bool,
    isInternal: PropTypes.bool,
    isReadOnly: PropTypes.bool
  }

  GroupDetail.defaultProps = {
    hasPermission: true,
    isInternal: false
  }

  GroupDetail.attributes = attributes;

  return GroupDetail;
}
